import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import API from "../../api/api";

const formatDate = (raw) => {
  if (!raw) return "-";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString();
};

const medalPillClass = (medal) => {
  const key = String(medal || "").toLowerCase();
  if (key === "gold") return "bg-yellow-100 text-yellow-700";
  if (key === "silver") return "bg-slate-100 text-slate-700";
  if (key === "bronze") return "bg-amber-100 text-amber-700";
  return "bg-blue-100 text-blue-700";
};

const CertificatesDownload = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadCertificates = async () => {
      setLoading(true);
      setError("");
      try {
        const rows = await API.fetchPlayerCertificates();
        if (!cancelled) {
          setCertificates(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load certificates.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCertificates();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = async (certificateId) => {
    setDownloadingId(certificateId);
    try {
      await API.downloadPlayerCertificate(certificateId);
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: err?.message || "Unable to download certificate right now.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <p className="text-gray-500 text-lg">Loading your certificates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mt-6 sm:mt-8 mobile-page-pad">
      <h1 className="text-xl sm:text-2xl font-bold text-blue-700 mb-6 text-center">
        My Certificates
      </h1>

      {certificates.length === 0 ? (
        <div className="text-center text-gray-500">
          No certificates have been issued for your account yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 rounded-lg text-xs sm:text-sm">
            <thead className="bg-blue-100 text-gray-700">
              <tr>
                <th className="p-3 border">Event</th>
                <th className="p-3 border">Sport</th>
                <th className="p-3 border">Game</th>
                <th className="p-3 border">Type</th>
                <th className="p-3 border">Serial No.</th>
                <th className="p-3 border">Issued On</th>
                <th className="p-3 border">Download</th>
              </tr>
            </thead>

            <tbody>
              {certificates.map((row) => (
                <tr key={row.id} className="text-center hover:bg-gray-50 transition">
                  <td className="p-3 border font-medium text-gray-800">
                    {row.event_title || "-"}
                  </td>
                  <td className="p-3 border">{row.sport_name || "-"}</td>
                  <td className="p-3 border">{row.game_name || "-"}</td>
                  <td className="p-3 border">
                    <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${medalPillClass(row.medal_type)}`}>
                      {row.medal_type || "Participation"}
                    </span>
                  </td>
                  <td className="p-3 border">{row.serial_no || "-"}</td>
                  <td className="p-3 border">
                    {formatDate(row.issued_at || row.event_date)}
                  </td>
                  <td className="p-3 border">
                    <button
                      onClick={() => handleDownload(row.id)}
                      disabled={downloadingId === row.id}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-xs px-3 py-1 rounded"
                    >
                      {downloadingId === row.id ? "Downloading..." : "Download"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CertificatesDownload;

